import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Timer, WifiOff, Wifi } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import localforage from 'localforage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple hash function for offline password verification
const simpleHash = async (str) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Load saved credentials if "Remember Me" was used
    const loadSavedCredentials = async () => {
      const saved = await localforage.getItem('savedLogin');
      if (saved) {
        setUsername(saved.username);
        setPassword(saved.password);
        setRememberMe(true);
      }
    };
    loadSavedCredentials();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Cache credentials after successful online login (for offline use)
  const cacheCredentials = async (username, password, user, token) => {
    const hashedPassword = await simpleHash(password);
    await localforage.setItem('cachedCredentials', {
      username: username.toLowerCase(),
      passwordHash: hashedPassword,
      user,
      token,
      cachedAt: new Date().toISOString()
    });
  };

  // Save login credentials if "Remember Me" is checked
  const saveLoginCredentials = async (username, password) => {
    if (rememberMe) {
      await localforage.setItem('savedLogin', { username, password });
    } else {
      await localforage.removeItem('savedLogin');
    }
  };

  // Attempt offline login using cached credentials
  const attemptOfflineLogin = async () => {
    const cached = await localforage.getItem('cachedCredentials');
    if (!cached) {
      toast.error('No cached login found. Please connect to the internet for first login.');
      return false;
    }

    const hashedInput = await simpleHash(password);
    if (cached.username === username.toLowerCase() && cached.passwordHash === hashedInput) {
      toast.success('Offline login successful!');
      onLogin(cached.user, cached.token);
      return true;
    } else {
      toast.error('Invalid credentials for offline login');
      return false;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    // If offline, try cached credentials
    if (!isOnline) {
      await attemptOfflineLogin();
      setLoading(false);
      return;
    }

    // Online login
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password,
        selected_role: selectedRole // Include selected role if chosen
      });

      const { access_token, user, available_roles, needs_role_selection } = response.data;
      
      // If user has multiple roles and hasn't selected one, show role selection
      if (needs_role_selection && available_roles && available_roles.length > 1) {
        setAvailableRoles(available_roles);
        setPendingCredentials({ username, password });
        setShowRoleSelection(true);
        setLoading(false);
        return;
      }
      
      // Cache credentials for offline use
      await cacheCredentials(username, password, user, access_token);
      
      // Save login if "Remember Me" is checked
      await saveLoginCredentials(username, password);
      
      toast.success('Login successful!');
      onLogin(user, access_token);
    } catch (error) {
      // If online request fails due to network, try offline login
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        toast.info('Network unavailable, trying offline login...');
        await attemptOfflineLogin();
      } else {
        toast.error(error.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async (role) => {
    if (!pendingCredentials) return;
    
    setLoading(true);
    setSelectedRole(role);
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: pendingCredentials.username,
        password: pendingCredentials.password,
        selected_role: role
      });

      const { access_token, user } = response.data;
      
      // Cache credentials for offline use
      await cacheCredentials(pendingCredentials.username, pendingCredentials.password, user, access_token);
      
      // Save login if "Remember Me" is checked
      await saveLoginCredentials(pendingCredentials.username, pendingCredentials.password);
      
      toast.success(`Logged in as ${role.charAt(0).toUpperCase() + role.slice(1)}!`);
      setShowRoleSelection(false);
      onLogin(user, access_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const roleDisplayNames = {
    admin: 'Administrator',
    scorer: 'Scorer',
    marshal: 'Marshal / Timer'
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1769348900282-f3b652f7db50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxyYWxseSUyMGNhciUyMGRpcnQlMjBhY3Rpb258ZW58MHx8fHwxNzcwODg2ODQyfDA&ixlib=rb-4.1.0&q=85')`
      }}
    >
      <div className="absolute inset-0 bg-black/80" />
      <div className="relative z-10 w-full max-w-md">
        <div className="glassmorphism rounded-lg p-8 shadow-2xl">
          <div className="flex flex-col items-center mb-8">
            {/* Timer/Stopwatch Icon */}
            <div className="bg-[#facc15] rounded-full p-4 mb-4">
              <Timer size={48} className="text-black" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white tracking-wider text-center">RALLY TIMING</h1>
            {/* Vosloo R Logo */}
            <img 
              src="/vosloo-logo.png" 
              alt="Vosloo R" 
              className="mt-3 h-8 w-auto object-contain"
              style={{ maxWidth: '120px' }}
            />
          </div>

          {/* Connection Status */}
          <div className={`flex items-center justify-center gap-2 mb-4 p-2 rounded ${isOnline ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
            {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
            <span className="text-sm font-medium">
              {isOnline ? 'Online' : 'Offline Mode'}
            </span>
          </div>

          {/* Role Selection UI */}
          {showRoleSelection ? (
            <div className="space-y-5" data-testid="role-selection-panel">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white mb-2">Select Your Role</h2>
                <p className="text-zinc-400 text-sm">You have access to multiple roles. Choose how you'd like to log in:</p>
              </div>
              
              <div className="space-y-4 py-4 px-2">
                {availableRoles.map((role) => (
                  <Button
                    key={role}
                    onClick={() => handleRoleSelect(role)}
                    disabled={loading}
                    className="w-full h-14 text-lg font-bold uppercase tracking-wider bg-zinc-700 hover:bg-zinc-600 text-white transition-all border border-zinc-600 my-2"
                    data-testid={`role-select-${role}`}
                  >
                    {loading && selectedRole === role ? 'LOGGING IN...' : roleDisplayNames[role] || role}
                  </Button>
                ))}
              </div>
              
              <Button
                onClick={() => {
                  setShowRoleSelection(false);
                  setAvailableRoles([]);
                  setPendingCredentials(null);
                  setSelectedRole(null);
                }}
                variant="ghost"
                className="w-full text-zinc-400 hover:text-white mt-4"
                data-testid="back-to-login-button"
              >
                ← Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <Label htmlFor="username" className="text-zinc-300 uppercase-label">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="mt-2 bg-[#18181b] border-[#27272a] text-white focus:border-[#facc15]"
                  placeholder="Enter username"
                  required
                  data-testid="login-username-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-zinc-300 uppercase-label">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 bg-[#18181b] border-[#27272a] text-white focus:border-[#facc15]"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-[#27272a] bg-[#18181b] text-[#facc15] focus:ring-[#facc15] focus:ring-offset-0"
                  data-testid="remember-me-checkbox"
                />
                <Label htmlFor="rememberMe" className="text-zinc-400 text-sm cursor-pointer">
                  Remember my login on this device
                </Label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 text-lg font-black uppercase tracking-widest bg-[#facc15] text-black hover:bg-[#eab308] active:scale-95 transition-transform"
                data-testid="login-submit-button"
              >
                {loading ? 'LOGGING IN...' : (isOnline ? 'LOGIN' : 'OFFLINE LOGIN')}
              </Button>
              
              {!isOnline && (
                <p className="text-xs text-center text-zinc-500">
                  Offline login uses cached credentials from your last successful login
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;